// FILE: /src/Components/Apps/CaptionResultCard.js

import React, { useState } from "react";
import { Button, Tag, Tooltip, message } from "antd";
import {
  CopyOutlined,
  ExpandAltOutlined,
  BranchesOutlined,
  CheckOutlined,
} from "@ant-design/icons";

export const getCaptionText = (caption) => {
  if (!caption) return "";

  if (typeof caption === "string") {
    return caption;
  }

  if (typeof caption.caption === "string") {
    return caption.caption;
  }

  if (typeof caption.text === "string") {
    return caption.text;
  }

  if (typeof caption.content === "string") {
    return caption.content;
  }

  if (typeof caption.generated_text === "string") {
    return caption.generated_text;
  }

  if (typeof caption.output === "string") {
    return caption.output;
  }

  if (typeof caption.raw?.caption === "string") {
    return caption.raw.caption;
  }

  if (typeof caption.raw?.text === "string") {
    return caption.raw.text;
  }

  if (Array.isArray(caption.raw?.captions) && caption.raw.captions.length > 0) {
    const firstCaption = caption.raw.captions[0];

    if (typeof firstCaption === "string") {
      return firstCaption;
    }

    return firstCaption?.caption || firstCaption?.text || "";
  }

  return "";
};

const getCaptionHashtags = (caption) => {
  if (!caption) return [];

  if (Array.isArray(caption.hashtags)) {
    return caption.hashtags;
  }

  if (Array.isArray(caption.raw?.hashtags)) {
    return caption.raw.hashtags;
  }

  return [];
};

const CaptionResultCard = ({
  caption,
  index,
  onExpand,
  onSimilar,
  onUse,
  loadingVariant = false,
}) => {
  const [copied, setCopied] = useState(false);
  const captionText = getCaptionText(caption);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(captionText);
      setCopied(true);
      message.success("Caption copied.");
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      message.error("Could not copy caption.");
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 bg-gradient-to-br from-white to-purple-50/40">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center ring-1 ring-purple-100">
            <i className="fa-solid fa-quote-left text-sm"></i>
          </div>

          <div>
            <h3 className="text-sm font-extrabold text-gray-900 mb-0">
              Caption {index + 1}
            </h3>
            <p className="text-[11px] text-gray-500 mb-0">
              {caption?.type || "generated"}
            </p>
          </div>
        </div>

        <Tag color="purple" className="m-0 rounded-full">
          AI
        </Tag>
      </div>

      <div className="p-4">
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap mb-0">
          {captionText || "No caption text returned."}
        </p>

        {getCaptionHashtags(caption).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {getCaptionHashtags(caption).map((tag) => (
              <span
                key={tag}
                className="text-xs bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2 py-0.5"
              >
                {String(tag).startsWith("#") ? tag : `#${tag}`}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Tooltip title="Copy caption">
            <Button
              size="small"
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </Tooltip>

          {onUse && (
            <Button size="small" type="primary" ghost onClick={() => onUse(captionText)}>
              Use
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="small"
            icon={<ExpandAltOutlined />}
            loading={loadingVariant}
            onClick={() => onExpand?.(captionText)}
          >
            Expand
          </Button>

          <Button
            size="small"
            icon={<BranchesOutlined />}
            loading={loadingVariant}
            onClick={() => onSimilar?.(captionText)}
          >
            Similar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaptionResultCard;